import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import * as api from "../api/client";
import DataTable from "../components/DataTable";
import RoleFormModal from "../components/RoleFormModal";

function fmtDate(epochSeconds) {
  return epochSeconds ? new Date(epochSeconds * 1000).toLocaleString() : "—";
}

// Admin-only. Lists roles in the same DataTable+modal pattern as UsersPage. The table list comes from
// the server (GET /api/tables) so it always matches what the backend will accept.
export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [tables, setTables] = useState([]);
  const [docFolders, setDocFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modal, setModal] = useState(null); // { mode: "create" | "edit", role? }
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [r, t, d] = await Promise.all([api.listRoles(), api.listTables(), api.listDocCategories()]);
      setRoles(r.roles || []);
      setTables(t.tables || []);
      setDocFolders(d.categories || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function closeModal() {
    setModal(null);
    setModalError("");
  }

  async function handleSubmit(payload) {
    setSaving(true);
    setModalError("");
    try {
      if (modal.mode === "edit") {
        await api.updateRole(modal.role.id, payload.tables, payload.columns, payload.docTags);
      } else {
        await api.createRole(payload.name, payload.tables, payload.columns, payload.docTags);
      }
      closeModal();
      await load();
    } catch (e) {
      setModalError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeRole(id, rname) {
    if (!window.confirm(`Delete role "${rname}"? Users who have it will lose that access.`)) return;
    setError("");
    try {
      await api.deleteRole(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  const columns = [
    { key: "name", label: "Role", sortable: true },
    {
      key: "tables",
      label: "Tables granted",
      // A table narrowed to some of its columns is badged with the count, so the list distinguishes a
      // partial grant from a whole-table one at a glance rather than only inside the edit modal.
      render: (r) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {(r.tables || []).length === 0 ? (
            <span style={{ color: "var(--muted)" }}>— none —</span>
          ) : (
            r.tables.map((t) => {
              const cols = (r.columns || {})[t];
              return (
                <span key={t} className={"pill " + (cols ? "warn" : "neutral")}
                      title={cols ? `Only these columns: ${cols.join(", ")}` : "All columns"}>
                  {t}
                  {cols ? ` · ${cols.length} cols` : ""}
                </span>
              );
            })
          )}
        </div>
      ),
    },
    {
      key: "docTags",
      label: "Documents",
      // Grants are stored by folder id; shown as "HR (all)" for a whole category or "HR › HR Policy".
      render: (r) => {
        const byId = Object.fromEntries(docFolders.map((f) => [f.id, f]));
        const names = (r.docTags || [])
          .map((id) => byId[id])
          .filter(Boolean)
          .map((f) => (f.parentId && byId[f.parentId] ? `${byId[f.parentId].name} › ${f.name}` : `${f.name} (all)`));
        return names.length === 0 ? (
          <span style={{ color: "var(--muted)" }}>— none —</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {names.map((n) => (
              <span key={n} className="pill info">
                {n}
              </span>
            ))}
          </div>
        );
      },
    },
    { key: "createdAt", label: "Created", sortable: true, render: (r) => fmtDate(r.createdAt) },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: (r) => (
        <div className="dt-actions">
          <button className="dt-icon-btn" title="Edit" onClick={() => setModal({ mode: "edit", role: r })}>
            ✎
          </button>
          <button className="dt-icon-btn danger" title="Delete" onClick={() => removeRole(r.id, r.name)}>
            🗑
          </button>
        </div>
      ),
    },
  ];

  return (
    <main className="main">
      <div className="topbar">
        <h1>Roles</h1>
        <p>
          Each role grants access to a set of dataset tables and document tags. Users are assigned a role on
          the Users page.
        </p>
      </div>
      <div className="page-body">
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}

        <div className="page-head-row">
          <Button variant="success" onClick={() => setModal({ mode: "create" })}>
            + Create role
          </Button>
        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : (
          <DataTable
            columns={columns}
            rows={roles}
            searchKeys={["name"]}
            searchPlaceholder="Search roles..."
            emptyMessage="No roles yet — create one above."
          />
        )}

        {modal && (
          <RoleFormModal
            mode={modal.mode}
            initial={modal.role}
            tables={tables}
            docFolders={docFolders}
            saving={saving}
            error={modalError}
            onClose={closeModal}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </main>
  );
}
